"use client"

import { useState, useEffect, useRef } from "react"
import { Avatar, Button, Popover, Switch, Divider, Tag, Modal } from "antd"
import {
  CreditCardOutlined,
  UserOutlined,
  GiftOutlined,
  MoonOutlined,
  SettingOutlined,
  FileTextOutlined,
  CustomerServiceOutlined,
  LogoutOutlined,
  RightOutlined,
  CheckOutlined,
  LoginOutlined,
} from "@ant-design/icons"
import { useEmbeddedAuth } from "../../contexts/EmbeddedAuthContext"
import userService from "../../services/userService"
import workspaceService from "../../services/workspaceService"
import authService from "../../services/authService"
import api from "../../services/api"
import endpoints from "../../services/endpoints"
import DiamondIcon from "../icons/DiamondIcon"
import CommandIcon from "../icons/CommandIcon"
import ShiftIcon from "../icons/ShiftIcon"
import VectorIcon from "../icons/VectorIcon"
import wxPubile from "@/styles/images/wx-pubile.jpg"
import wxHelp from "@/styles/images/wx-help.jpg"

const UserInfoArea = ({ isCollapsed }) => {
  const { showLoginModal } = useEmbeddedAuth()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [userPopoverVisible, setUserPopoverVisible] = useState(false)
  const [workspacePopoverVisible, setWorkspacePopoverVisible] = useState(false)
  const [darkMode, setDarkMode] = useState(false)
  const [workspaces, setWorkspaces] = useState([])
  const [workspacesLoading, setWorkspacesLoading] = useState(true)
  const [currentWorkspace, setCurrentWorkspace] = useState(null)
  const [helpModalVisible, setHelpModalVisible] = useState(false)
  const [helpModalType, setHelpModalType] = useState(null) // 'doc' or 'service'
  const [logoutLoading, setLogoutLoading] = useState(false);
  const userFetchRef = useRef(false);

  const fetchUserFromApi = async () => {
    const token = authService.getToken?.()
    if (!token || userFetchRef.current) return
    userFetchRef.current = true
    try {
      const profile = await api.get(endpoints.auth.me)
      const userProfile = profile?.user || profile
      if (userProfile && userProfile.id) {
        authService.saveAuth({ user: userProfile })
        const savedUser = authService.getCurrentUser?.() || userProfile
        setUser(savedUser)
        setCurrentWorkspace(savedUser?.workspace || null)
      }
    } catch (error) {
      console.error("????????:", error)
    } finally {
      userFetchRef.current = false
    }
  }

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        setLoading(true)
        // ??? localStorage ??
        const userStr = localStorage.getItem('syntrust_user')
        const userData = userStr ? JSON.parse(userStr) : null
        console.log('fetchCurrentUser-userData', userData);
        if (userData) {
          setUser(userData)
          setCurrentWorkspace(userData?.workspace || null)
          return
        }

        // ??? access_token???? user???
        await fetchUserFromApi()
      } catch (error) {
        console.error("????????:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchCurrentUser()

    // Add keyboard shortcut listener for logout
    const handleKeyDown = (event) => {
      // Check for Cmd+Shift+Q (Mac) or Ctrl+Shift+Q (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key === "q") {
        handleLogout()
      }
    }

    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [])

  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        setWorkspacesLoading(true)
        const workspacesData = await workspaceService.getWorkspaces()
        setWorkspaces(workspacesData)
      } catch (error) {
        console.error("获取工作区列表失败:", error)
      } finally {
        setWorkspacesLoading(false)
      }
    }

    fetchWorkspaces()
  }, [])

  useEffect(() => {
    const handleStorage = () => {
      try {
        const userStr = localStorage.getItem('syntrust_user');
        const userObj = userStr ? JSON.parse(userStr) : null;
        setUser(userObj);
        // setCurrentWorkspace(userObj?.workspace || null);
        if (!userObj) {
          fetchUserFromApi()
        }
      } catch {}
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(handleStorage, 500);
    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(interval);
    };
  }, []);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await authService.logout();
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleDarkModeToggle = (checked) => {
    setDarkMode(checked)
    // Implement dark mode toggle functionality
  }

  const handleWorkspaceChange = async (workspaceId) => {
    try {
      // 本地切换：找到目标workspace对象
      const target = workspaces.find(ws => ws.id === workspaceId)
      if (target) {
        const userStr = localStorage.getItem('syntrust_user')
        if (userStr) {
          const userObj = JSON.parse(userStr)
          userObj.workspace = target
          localStorage.setItem('syntrust_user', JSON.stringify(userObj))
        }
      }
      window.location.reload()
    } catch (error) {
      console.error(`切换工作区失败 (ID: ${workspaceId}):`, error)
    }
    setWorkspacePopoverVisible(false)
  }

  // 获取当前激活的workspaceId
  const getActiveWorkspaceId = () => {
    try {
      const userStr = localStorage.getItem('syntrust_user');
      if (userStr) {
        const userObj = JSON.parse(userStr);
        return userObj.workspace?.id;
      }
    } catch {}
    return null;
  };
  const activeWorkspaceId = getActiveWorkspaceId();

  // User profile popover content
  const userPopoverContent = (
    <div className="p-4 w-64">
      {/* User header */}
      <div className="flex justify-between items-center mb-3">
        <div className="flex items-center">
          <Avatar size={48} className={`mr-3 ${user && user.avatar ? '' : 'bg-yellow-400'}`} src={user && user.avatar ? user.avatar : undefined}>
            {(!user || !user.avatar) ? (user && (user.name || user.username) ? (user.name || user.username).charAt(0).toUpperCase() : "U") : null}
          </Avatar>
          <div>
            <div
              className="font-medium max-w-[120px] truncate"
              title={user ? (user.name || user.username) : "未知用户"}
            >
              {user ? (user.name || user.username) : "未知用户"}
            </div>
            <div className="text-xs text-gray-500">{user ? user.email : ""}</div>
          </div>
        </div>
        <div>
          <Avatar size={24} className="bg-gray-100 text-black">
            <DiamondIcon style={{ fontSize: "14px" }} />
          </Avatar>
        </div>
      </div>

      <Divider className="my-3" />

      {/* Menu items */}
      <div className="space-y-3">
        {/* <div className="flex justify-between items-center">
          <div className="flex items-center">
            <CreditCardOutlined className="mr-3 text-gray-600" />
            <span>个人版本</span>
          </div>
          <Button size="small" type="primary">
            立即升级
          </Button>
        </div> */}

        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <UserOutlined className="mr-3 text-gray-600" />
            <span style={{ cursor: 'pointer' }} onClick={() => { window.location.href =  `/personal-info/${user.id}`}}>个人主页</span>
          </div>
          {/* <Tag color="success" className="m-0">
            完善信息得奖励
          </Tag> */}
        </div>

        {/* <div className="flex justify-between items-center">
          <div className="flex items-center">
            <GiftOutlined className="mr-3 text-gray-600" />
            <span>获取免费token</span>
            <Tag color="error" className="ml-2 m-0">
              HOT
            </Tag>
          </div>
          <RightOutlined className="text-gray-400" />
        </div> */}

        {/* <div className="flex justify-between items-center">
          <div className="flex items-center">
            <MoonOutlined className="mr-3 text-gray-600" />
            <span>深色模式</span>
          </div>
          <Switch size="small" checked={darkMode} onChange={handleDarkModeToggle} />
        </div> */}

        {/* <div className="flex justify-between items-center">
          <div className="flex items-center">
            <SettingOutlined className="mr-3 text-gray-600" />
            <span>设置</span>
          </div>
        </div> */}

        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <FileTextOutlined className="mr-3 text-gray-600" />
            <span style={{ cursor: 'pointer' }} onClick={() => { setHelpModalType('doc'); setHelpModalVisible(true); }}>帮助文档</span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <CustomerServiceOutlined className="mr-3 text-gray-600" />
            <span style={{ cursor: 'pointer' }} onClick={() => { setHelpModalType('service'); setHelpModalVisible(true); }}>客服中心</span>
          </div>
        </div>
      </div>

      <Divider className="my-3" />

      {/* Logout */}
      <div className="flex justify-between items-center cursor-pointer" onClick={handleLogout}>
        <div className="flex items-center">
          <LogoutOutlined className="mr-3 text-gray-600" />
          <span>{logoutLoading ? '正在退出...' : '退出登录'}</span>
        </div>
      </div>
    </div>
  )

  // Workspace popover content
  const workspacePopoverContent = (
    <div className="p-4 w-64">
      <div className="font-medium mb-3">切换工作区</div>
      <div className="space-y-2 max-h-60 overflow-y-auto">
        {workspacesLoading ? (
          <div className="py-4 text-center">加载中...</div>
        ) : workspaces.length === 0 ? (
          <div className="py-4 text-center">暂无工作区</div>
        ) : (
          workspaces.map((workspace) => (
            <div
              key={workspace.id}
              className={`flex justify-between items-center p-2 rounded cursor-pointer hover:bg-gray-100 ${
                activeWorkspaceId === workspace.id ? "bg-gray-50" : ""
              }`}
              onClick={() => handleWorkspaceChange(workspace.id)}
            >
              <div className="flex items-center">
                <Avatar size={32} className="mr-3 bg-blue-100">
                  {workspace.icon || (workspace.name ? workspace.name.charAt(0) : "W")}
                </Avatar>
                <div>
                  <div className="font-medium">{workspace.name || "未命名工作区"}</div>
                  <div className="text-xs text-gray-500">{workspace.role || "成员"}</div>
                </div>
              </div>
              {activeWorkspaceId === workspace.id && <CheckOutlined className="text-blue-500" />}
            </div>
          ))
        )}
      </div>
      <div className="mt-3">
        <Button type="dashed" block icon={<span>+</span>}>
          创建新工作区
        </Button>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 border-t border-gray-200 ${isCollapsed ? "h-16" : "h-16"}`}>
        <div>加载中...</div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className={`flex items-center p-2 border-t border-gray-200 ${isCollapsed ? "h-16" : "h-16"}`}>
        <Button 
          icon={isCollapsed ? <LoginOutlined style={{ color: 'white' }} /> : null}
          onClick={() => showLoginModal()}
          block={!isCollapsed}
          title="点击登录"
          className="transition-all duration-300 ease-in-out"
          style={{
            backgroundColor: '#000000', 
            color: '#FFFFFF', 
            border: '1px solid #333333',
            ...(isCollapsed ? { width: '32px', height: '32px', padding: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' } : {})
          }}
        >
          {isCollapsed ? "" : "点击登录"}
        </Button>
      </div>
    )
  }

  return (
    <>
    <div className={`border-t border-gray-200 p-2`}>
      <div className={`flex items-center justify-between`}>
        <Popover
          content={userPopoverContent}
          trigger="click"
          open={userPopoverVisible}
          onOpenChange={setUserPopoverVisible}
          placement="topRight"
          overlayClassName="user-profile-popover"
          getPopupContainer={() => document.body}
          autoAdjustOverflow={true}
        >
          <div
            className={`flex items-center cursor-pointer ${isCollapsed ? "" : "w-full"}`}
            onClick={() => setUserPopoverVisible(true)}
          >
            <div className="relative">
              <Avatar className={`${user && user.avatar ? '' : 'bg-yellow-400'} text-white`} size={32} src={user && user.avatar ? user.avatar : undefined}>
                {(!user || !user.avatar) && user && user.name ? user.name.charAt(0) : (!user || !user.avatar) ? "U" : null}
              </Avatar>
            </div>
            {!isCollapsed && (
              <div className="ml-2 overflow-hidden flex flex-col min-w-0">
                <div
                  className="font-medium text-sm truncate max-w-[120px]"
                  title={user ? user.name : "未知用户"}
                >
                  {user ? user.name : "未知用户"}
                </div>
                <div className="text-xs text-gray-500 truncate max-w-[120px]" title={user ? user.email : ""}>
                  {user ? user.email : ""}
                </div>
              </div>
            )}
          </div>
        </Popover>

        {/* {!isCollapsed && (
          <div className="flex items-center">
            <div className="w-[1px] h-8 bg-gray-300 mx-2"></div>
            <Popover
              content={workspacePopoverContent}
              trigger="click"
              open={workspacePopoverVisible}
              onOpenChange={setWorkspacePopoverVisible}
              placement="topRight"
              overlayClassName="workspace-popover"
              getPopupContainer={() => document.body}
              autoAdjustOverflow={true}
            >
              <Button
                type="text"
                className="flex items-center justify-center p-0 w-6 h-6 min-w-0"
                onClick={(e) => {
                  e.stopPropagation()
                  setWorkspacePopoverVisible(true)
                }}
              >
                <VectorIcon />
              </Button>
            </Popover>
          </div>
        )} */}
      </div>
    </div>
      {/* 帮助文档/客服中心 Modal */}
      <Modal
        open={helpModalVisible}
        title={helpModalType === 'doc' ? '帮助文档' : helpModalType === 'service' ? '客服中心' : ''}
        footer={null}
        onCancel={() => setHelpModalVisible(false)}
        centered
      >
        {helpModalType === 'doc' && (
          <>
            <img src={wxPubile} alt="帮助文档二维码" style={{ width: '100%', maxWidth: 320, margin: '0 auto', display: 'block' }} />
            <div style={{ textAlign: 'center', marginTop: 16, color: '#666' }}>
              扫码关注公众号，获取平台最新动态与帮助文档
            </div>
          </>
        )}
        {helpModalType === 'service' && (
          <>
            <img src={wxHelp} alt="客服中心二维码" style={{ width: '100%', maxWidth: 320, margin: '0 auto', display: 'block' }} />
            <div style={{ textAlign: 'center', marginTop: 16, color: '#666' }}>
              扫码添加客服微信，获取一对一支持与解答
            </div>
          </>
        )}
      </Modal>
    </>
  )
}

export default UserInfoArea
